import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import AdminBillingNav from '@/components/adminBilling/AdminBillingNav';
import ConfirmActionModal from '@/components/ConfirmActionModal';
import { useToast } from '@/components/Toast';
import { adminBillingService, type AdminBillingAttention } from '@/api/services/adminBilling';
import { formatApiError } from '@/utils/formatApiError';
import { useMe } from '@/context/MeContext';
import { DataTableScrollArea } from '@/components/table';

function AttentionSection({
  title,
  rows,
  attentionKey,
  onDismiss,
}: {
  title: string;
  rows: Record<string, unknown>[];
  attentionKey?: string;
  onDismiss?: (key: string, orgId: string) => void;
}) {
  if (!rows.length) return null;
  return (
    <section className="panel">
      <h2>{title} ({rows.length})</h2>
      <DataTableScrollArea>
        <table className="data-table">
        <thead>
          <tr>
            <th>Organization</th>
            <th>Plan</th>
            <th>Expires / Grace</th>
            {onDismiss ? <th /> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={String(r.organizationId || r.id)}>
              <td>
                {r.organizationId ? (
                  <Link to={`/admin/billing/organizations/${r.organizationId}`}>
                    {String(r.organizationName || r.organizationSlug || r.organizationId)}
                  </Link>
                ) : (
                  String(r.provider || r.eventType || r.id)
                )}
              </td>
              <td>{String(r.planSlug || r.provider || '—')}</td>
              <td>
                {r.expiresAt ? new Date(String(r.expiresAt)).toLocaleDateString() : null}
                {r.graceUntil ? new Date(String(r.graceUntil)).toLocaleDateString() : null}
                {r.error ? String(r.error) : null}
                {r.notificationsToday != null ? `${r.notificationsToday} notifications today` : null}
              </td>
              {onDismiss && attentionKey && r.organizationId ? (
                <td>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => onDismiss(attentionKey, String(r.organizationId))}
                  >
                    Dismiss
                  </button>
                </td>
              ) : onDismiss ? <td /> : null}
            </tr>
          ))}
        </tbody>
      </table>
        </DataTableScrollArea>
    </section>
  );
}

export default function AdminBillingAttentionPage() {
  const { me } = useMe();
  const { toast } = useToast();
  const canManage = me?.permissions.canManageAdminBilling;
  const [data, setData] = useState<AdminBillingAttention | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dismissTarget, setDismissTarget] = useState<{ key: string; orgId: string } | null>(null);
  const [dismissBusy, setDismissBusy] = useState(false);

  const load = () => {
    adminBillingService.attention().then(setData).catch((e) => setError(formatApiError(e)));
  };

  useEffect(() => {
    load();
  }, []);

  const requestDismiss = (key: string, orgId: string) => {
    setDismissTarget({ key, orgId });
  };

  const executeDismiss = async () => {
    if (!dismissTarget) return;
    setDismissBusy(true);
    try {
      await adminBillingService.dismissAttention({
        attentionKey: dismissTarget.key,
        organizationId: dismissTarget.orgId,
      });
      toast('Attention item dismissed', 'success');
      load();
    } catch (e) {
      const message = formatApiError(e);
      setError(message);
      toast(message, 'error');
    } finally {
      setDismissBusy(false);
      setDismissTarget(null);
    }
  };

  const isPlatform =
    me?.role === 'owner' || me?.role === 'platform_admin' || me?.role === 'admin';
  if (!isPlatform) return <Navigate to="/admin/billing" replace />;

  return (
    <div className="admin-billing-page">
      <AdminBillingNav />
      <PageHeader title="Billing attention" subtitle="Items requiring platform operator follow-up." />
      {error ? <div className="alert alert-error">{error}</div> : null}
      {!data ? <div className="panel">Loading…</div> : (
        <>
          <AttentionSection title="Payment failed / grace" rows={data.paymentFailed} />
          <AttentionSection title="Grace ending" rows={data.graceEnding} attentionKey="grace" onDismiss={canManage ? requestDismiss : undefined} />
          <AttentionSection title="Expiring tomorrow" rows={data.expiringTomorrow} />
          <AttentionSection title="Expiring soon" rows={data.expiringSoon} attentionKey="expiring" onDismiss={canManage ? requestDismiss : undefined} />
          <AttentionSection title="Expired" rows={data.expired} attentionKey="expired" onDismiss={canManage ? requestDismiss : undefined} />
          <AttentionSection title="Manual grants" rows={data.manualGrants} />
          <AttentionSection title="Webhook failures" rows={data.webhookFailures} />
          <AttentionSection title="Provider sync errors" rows={data.providerSyncErrors} />
          <AttentionSection title="High notification usage" rows={data.quotaAbuse} />
          <AttentionSection title="Disabled organizations" rows={data.organizationDisabled} />
        </>
      )}
      <ConfirmActionModal
        open={Boolean(dismissTarget)}
        title="Dismiss attention item?"
        description="This item will be hidden from the billing attention list until it reappears."
        confirmLabel="Dismiss"
        loading={dismissBusy}
        onCancel={() => setDismissTarget(null)}
        onConfirm={executeDismiss}
      />
    </div>
  );
}
